/*
  SMA — Header enhancements
  ---------------------------------------------------------------------------
  1. <sma-header-drawer> — accordion drawer navigation. Extends Dawn's
     HeaderDrawer but replaces the sliding-panel submenu behaviour with
     in-place expand/collapse, so there is no "back" step.
  2. Transparent header — toggles .sma-header--scrolled on the wrapper so the
     header can fade from transparent to its normal solid style on scroll.
*/
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     1. Accordion drawer
     ---------------------------------------------------------------------- */
  function defineDrawer() {
    const Base = customElements.get('header-drawer');
    if (!Base || customElements.get('sma-header-drawer')) return !!Base;

    customElements.define(
      'sma-header-drawer',
      class SmaHeaderDrawer extends Base {
        // Dawn binds every button in the drawer to "close submenu". The
        // accordion has no back buttons, so only re-bind the summaries.
        bindEvents() {
          this.querySelectorAll('summary').forEach((summary) =>
            summary.addEventListener('click', this.onSummaryClick.bind(this))
          );
          this.querySelectorAll(
            '.menu-drawer__utility-links button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
          ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
        }

        onSummaryClick(event) {
          const summaryElement = event.currentTarget;
          const detailsElement = summaryElement.parentNode;

          // The top-level hamburger keeps Dawn's open/close drawer behaviour.
          if (detailsElement === this.mainDetailsToggle) {
            super.onSummaryClick(event);
            return;
          }

          // Submenus: expand/collapse in place. No panel slide, no focus trap.
          event.preventDefault();

          const isOpen = detailsElement.hasAttribute('open');

          if (isOpen) {
            this.collapse(detailsElement);
          } else {
            // Close sibling branches so only one is open per level.
            const parentList = detailsElement.closest('ul');
            if (parentList) {
              parentList.querySelectorAll(':scope > li > details[open]').forEach((sibling) => {
                if (sibling !== detailsElement) this.collapse(sibling);
              });
            }
            detailsElement.setAttribute('open', '');
            detailsElement.classList.add('menu-opening');
            summaryElement.setAttribute('aria-expanded', 'true');
          }
        }

        collapse(detailsElement) {
          detailsElement.classList.remove('menu-opening');
          const summary = detailsElement.querySelector(':scope > summary');
          if (summary) summary.setAttribute('aria-expanded', 'false');
          // Collapse nested branches too, so reopening starts clean.
          detailsElement.querySelectorAll('details[open]').forEach((child) => {
            child.classList.remove('menu-opening');
            const childSummary = child.querySelector(':scope > summary');
            if (childSummary) childSummary.setAttribute('aria-expanded', 'false');
            child.removeAttribute('open');
          });
          detailsElement.removeAttribute('open');
        }

        // Dawn closes the whole drawer on Escape or when a submenu loses
        // focus; for an accordion only the branch should close.
        onKeyUp(event) {
          if (event.code.toUpperCase() !== 'ESCAPE') return;

          const openDetails = event.target.closest('details[open]');
          if (!openDetails) return;

          if (openDetails === this.mainDetailsToggle) {
            this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
          } else {
            this.collapse(openDetails);
          }
        }
      }
    );

    return true;
  }

  // global.js is deferred too; if it has not run yet, wait for it.
  if (!defineDrawer()) {
    document.addEventListener('DOMContentLoaded', defineDrawer);
    window.addEventListener('load', defineDrawer);
  }

  /* ----------------------------------------------------------------------
     2. Transparent header — solid on scroll
     ---------------------------------------------------------------------- */
  var scrollHandler = null;

  function initTransparentHeader() {
    const wrapper = document.querySelector('.sma-header--transparent');
    if (!wrapper) {
      // Header re-rendered without the transparent class — drop the old handler.
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
      }
      return;
    }

    // A section reload replaces the wrapper element, so rebind onto the new
    // one rather than stacking another listener over a detached node.
    if (scrollHandler) window.removeEventListener('scroll', scrollHandler);

    // Opening the drawer deliberately leaves the header untouched — logo,
    // icon colors and background all stay as they are.
    const threshold = 20;
    let ticking = false;

    const apply = function () {
      const scrolled = (window.pageYOffset || document.documentElement.scrollTop) > threshold;
      wrapper.classList.toggle('sma-header--scrolled', scrolled);
      ticking = false;
    };

    scrollHandler = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });

    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTransparentHeader);
  } else {
    initTransparentHeader();
  }

  // The script is loaded from inside the header section, so it re-executes on
  // every header re-render. Bind the section hook once per page.
  if (!window.__smaHeaderSectionHook) {
    window.__smaHeaderSectionHook = true;
    document.addEventListener('shopify:section:load', function () {
      defineDrawer();
      initTransparentHeader();
    });
  }
})();